/**
 * Period and date helpers.
 *
 * Lives in shared because the server generates the labels that the client renders
 * — if these two ever drifted, a chart axis and its table header would disagree.
 * Everything here is pure string arithmetic: no Date objects, no timezones.
 */
export const MONTH_ABBR_EN = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
export const MONTH_ABBR_ID = [
    'Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun',
    'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des',
];
/** Full Indonesian month names, as they appear in the analyst workbook headers. */
export const MONTH_FULL_ID = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
];
const PERIOD_RE = /^(\d{4})-(0[1-9]|1[0-2])$/;
const DATE_RE = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
export function isPeriod(value) {
    return PERIOD_RE.test(value);
}
export function isIsoDate(value) {
    return DATE_RE.test(value);
}
/** `2026-06` -> `{ year: 2026, month: 6 }`. Throws on malformed input. */
export function parsePeriod(period) {
    const m = PERIOD_RE.exec(period);
    if (!m)
        throw new Error(`Invalid period "${period}", expected YYYY-MM`);
    return { year: Number(m[1]), month: Number(m[2]) };
}
export function formatPeriod(year, month) {
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
}
/** `2026-06-30` -> `2026-06`. */
export function periodOfDate(date) {
    return date.slice(0, 7);
}
/** `2026-06` -> `Jun 2026`. */
export function periodLabelEn(period) {
    const { year, month } = parsePeriod(period);
    return `${MONTH_ABBR_EN[month - 1]} ${year}`;
}
/** `2026-06` -> `Jun 2026`, `2025-12` -> `Des 2025`. */
export function periodLabelId(period) {
    const { year, month } = parsePeriod(period);
    return `${MONTH_ABBR_ID[month - 1]} ${year}`;
}
/** Shifts a period by a signed number of months. `addMonths('2026-01', -1)` -> `2025-12`. */
export function addMonths(period, delta) {
    const { year, month } = parsePeriod(period);
    const zeroBased = year * 12 + (month - 1) + delta;
    return formatPeriod(Math.floor(zeroBased / 12), (zeroBased % 12) + 1);
}
/** Every period from `from` to `to` inclusive, ascending. */
export function periodRange(from, to) {
    const out = [];
    let cursor = from;
    // Guard against an inverted range producing an infinite loop.
    for (let i = 0; i < 1200 && cursor <= to; i++) {
        out.push(cursor);
        cursor = addMonths(cursor, 1);
    }
    return out;
}
/**
 * Percent change from `previous` to `current`, in PERCENT units.
 * Returns null when the base is missing or zero — never Infinity, never NaN.
 */
export function pctChange(current, previous) {
    if (current == null || previous == null || previous === 0)
        return null;
    return ((current - previous) / previous) * 100;
}
//# sourceMappingURL=periods.js.map